import { useState } from "react";
import { createPortal } from "react-dom";
import { Share2, MessageCircle, Send, Copy, Check, X } from "lucide-react";
import { trackShare } from "@/hooks/useAnalytics";

/**
 * ShareToGroup - Auto-format messages for WhatsApp/Telegram
 * Uses Portal to render dropdown at document body level (fixes z-index issues)
 */

// Generate UTM link - uses current window location for correct domain
function generateUTMLink(source, medium, campaign) {
  // Get base URL from current location (works for both preview and production)
  const baseUrl = typeof window !== 'undefined' 
    ? `${window.location.protocol}//${window.location.host}`
    : "https://www.upnextcalendars.com";
  
  const params = new URLSearchParams({
    utm_source: source,
    utm_medium: medium,
    utm_campaign: campaign,
    utm_content: new Date().toISOString().split('T')[0]
  });
  return `${baseUrl}?${params.toString()}`;
}

// Format events into shareable message
function formatShareMessage(events, listTitle, listType = "general") {
  const header = getHeader(listTitle, listType);
  const eventLines = events.slice(0, 5).map(e => {
    const date = new Date(e.date);
    const day = date.getDate();
    const month = date.toLocaleDateString("ms-MY", { month: "short" });
    return `📅 ${day} ${month} - ${e.title}`;
  }).join("\n");
  
  const utmLink = generateUTMLink("social_share", listType, `quicklist_${listType}`);
  const footer = `\n\n🔗 ${utmLink}`;
  
  return `${header}\n\n${eventLines}${footer}`;
}

function getHeader(title, type) {
  switch(type) {
    case "weekend":
      return `🗓️ *${title}*\nJom plan sama-sama! 👇`;
    case "free":
      return `✅ *${title}*\nSemua ni PERCUMA! 👇`;
    case "movies":
      return `🎬 *${title}*\nFilem best nak tengok! 👇`;
    case "concerts":
      return `🎤 *${title}*\nKonsert tahun ni! 👇`;
    case "bored":
      return `😴 *Bosan? Cuba ni:*\nIdea random untuk korang 👇`;
    default:
      return `📌 *${title}*`;
  }
}

// Dropdown Menu Component (rendered via Portal)
function ShareDropdown({ isOpen, onClose, events, listTitle, listType, buttonRect }) {
  const [copied, setCopied] = useState(false);
  
  const message = formatShareMessage(events, listTitle, listType);
  const encodedMessage = encodeURIComponent(message);
  const eventTitle = events[0]?.title || listTitle;

  const shareToWhatsApp = () => {
    trackShare("whatsapp", events[0]?.id, listType, eventTitle);
    window.open(`https://wa.me/?text=${encodedMessage}`, "_blank");
    onClose();
  };

  const shareToTelegram = () => {
    trackShare("telegram", events[0]?.id, listType, eventTitle);
    window.open(`https://t.me/share/url?text=${encodedMessage}`, "_blank");
    onClose();
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(message);
      trackShare("copy", events[0]?.id, listType, eventTitle);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (!isOpen) return null;

  // Calculate position
  const menuStyle = {
    position: 'fixed',
    top: buttonRect ? buttonRect.bottom + 8 : 100,
    right: buttonRect ? window.innerWidth - buttonRect.right : 16,
    zIndex: 9999,
  };

  return createPortal(
    <>
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            style={{ zIndex: 9998 }}
            onClick={onClose}
            aria-hidden="true"
          />
          
          {/* Menu */}
          <div
            style={menuStyle}
            className="w-72 max-w-[calc(100vw-32px)]"
            role="dialog"
            aria-label="Share options"
          >
            <div className="bg-zinc-900 rounded-2xl border border-white/20 shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="px-4 py-3 border-b border-white/10 bg-white/5 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-white">Share ke group</p>
                  <p className="text-xs text-zinc-400">Message siap format</p>
                </div>
                <button 
                  onClick={onClose}
                  className="p-1.5 rounded-full hover:bg-white/10 text-zinc-400"
                  aria-label="Close share menu"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              
              {/* Options */}
              <div className="p-3 space-y-2">
                {/* WhatsApp */}
                <button
                  onClick={shareToWhatsApp}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-all active:scale-[0.98]"
                  aria-label="Share to WhatsApp"
                >
                  <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg shadow-[#25D366]/30">
                    <MessageCircle className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-white">WhatsApp</p>
                    <p className="text-xs text-zinc-400">Hantar ke group</p>
                  </div>
                </button>

                {/* Telegram */}
                <button
                  onClick={shareToTelegram}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-[#0088cc]/10 hover:bg-[#0088cc]/20 transition-all active:scale-[0.98]"
                  aria-label="Share to Telegram"
                >
                  <div className="w-10 h-10 rounded-full bg-[#0088cc] flex items-center justify-center shadow-lg shadow-[#0088cc]/30">
                    <Send className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-white">Telegram</p>
                    <p className="text-xs text-zinc-400">Hantar ke group</p>
                  </div>
                </button>

                {/* Copy */}
                <button
                  onClick={copyToClipboard}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-violet-500/10 hover:bg-violet-500/20 transition-all active:scale-[0.98]"
                  aria-label="Copy message to clipboard"
                >
                  <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
                    {copied ? (
                      <Check className="h-5 w-5 text-white" />
                    ) : (
                      <Copy className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-white">
                      {copied ? "Copied!" : "Copy Text"}
                    </p>
                    <p className="text-xs text-zinc-400">
                      {copied ? "Paste anywhere" : "Salin ke clipboard"}
                    </p>
                  </div>
                </button>
              </div>

              {/* Preview */}
              <div className="px-4 py-3 border-t border-white/10 bg-black/20">
                <p className="text-xs text-zinc-500 mb-2">Preview message:</p>
                <div className="p-3 rounded-lg bg-black/30 border border-white/5">
                  <pre className="text-xs text-zinc-300 whitespace-pre-wrap font-sans leading-relaxed">
                    {message.slice(0, 120)}...
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>,
    document.body
  );
}

// Share button component
export function ShareButton({ events, listTitle, listType = "general", variant = "icon" }) {
  const [showMenu, setShowMenu] = useState(false);
  const [buttonRect, setButtonRect] = useState(null);

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setButtonRect(rect);
    setShowMenu(!showMenu);
  };

  if (events.length === 0) return null;

  return (
    <>
      <button
        onClick={handleClick}
        className={`
          flex items-center gap-1.5 text-xs font-medium transition-all
          ${variant === "icon" 
            ? "p-2 rounded-lg hover:bg-white/10 text-muted-foreground hover:text-foreground" 
            : "px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-emerald-400 hover:from-emerald-500/30 hover:to-teal-500/30 active:scale-95"
          }
        `}
        data-testid={`share-${listType}`}
        aria-label={`Share ${listTitle} to group`}
      >
        <Share2 className="h-3.5 w-3.5" />
        {variant !== "icon" && <span>Send to Group</span>}
      </button>

      <ShareDropdown 
        isOpen={showMenu}
        onClose={() => setShowMenu(false)}
        events={events}
        listTitle={listTitle}
        listType={listType}
        buttonRect={buttonRect}
      />
    </>
  );
}

export { generateUTMLink };
